/*
 *+------------------------------------------------------------------------+
 *| Licensed Materials - Property of IBM
 *| IBM Cognos Products: Explore
 *| (C) Copyright IBM Corp. 2018, 2020
 *|
 *| US Government Users Restricted Rights - Use, duplication or disclosure
 *| restricted by GSA ADP Schedule Contract with IBM Corp.
 *+------------------------------------------------------------------------+
 */

import { types, getEnv } from 'mobx-state-tree';
import { FetchState } from 'util/ModelUtil';
const SUPPORTED_ACTIONS = ['deleteWidget'];

export default types
	.model('ExploreToolbarModel', {
		state: FetchState
	})
	.volatile(() => ({
		widgetActions: [],
		items: [],
		_disposeEvents: [],
		isHidden: false
	}))
	.views((self) => ({
		getItems() {
			if (self.state === 'initial') {
				self.fetchItems();
			}
			return self.items;
		},
		getCardActions(cards) {
			return self.cardActions.getActions(cards)
				.filter(action => SUPPORTED_ACTIONS.indexOf(action.id) >= 0);
		},
		getWidgetActions() {
			return self.widgetActions;
		},
		getIsHidden() {
			return self.isHidden;
		}
	}))
	.actions((self) => ({
		afterCreate() {
			const dashboardApi = getEnv(self).dashboardApi;

			if (dashboardApi) {
				self._disposeEvents.push(
					// Update the toolbar items when they have changed.
					dashboardApi.on('widget:toolbarItems:changed', () => {
						if (dashboardApi && dashboardApi.getCanvas() && dashboardApi.getCanvas().getSelectedContentList().length) {
							self.fetchWidgetActions();
						}
					}),
					// Update the toolbar hidden and fetch filter count when filter dock toggle has changed
					dashboardApi.on('filterDock:toggleCollapse', () => {
						self.toggleHidden();
					}),
					// Update the toolbar hidden and fetch filter count when filter dock is collapsed
					dashboardApi.on('filterDock:collapse', () => {
						self.toggleHidden(false);
					}),
					// Update the toolbar hidden and fetch filter count when filter dock is expanded
					dashboardApi.on('filterDock:expand', () => {
						self.toggleHidden(true);
					})
				);

				const lifeCycleManager = dashboardApi.getFeature && dashboardApi.getFeature('.LifeCycleManager');
				lifeCycleManager && lifeCycleManager.registerLifeCycleHandler('post:dashboard.initialize', () => {
					const lasso = dashboardApi.getFeature('Lasso');
					if(lasso){
						self._disposeEvents.push(lasso.on('change:toggle', () => self.fetchWidgetActions()));
					}
				});
			}
		},
		beforeDestroy() {
			// Remove the events.
			if( self._disposeEvents ) {
				self._disposeEvents.forEach( (dispose) => {
					if( dispose ) {
						dispose.remove();
					}
				});
			}
		},
		resetActions() {
			self.widgetActions = [];
		},
		setCardActions(cardActions) {
			self.cardActions = cardActions;
		},
		fetchWidgetActions() {
			const dashboardApi = getEnv(self).dashboardApi;
			// TODO:	if we remove getToolbarActions() and all actions come through getContentActionList(), then we should remove the mapping of
			//  		action['action'] and change the calling function in ExploreContainterToolbar -> _onPrimaryAction() to call actions.apply
			const widgetActions = (dashboardApi && dashboardApi.getToolbarActions() ? dashboardApi.getToolbarActions().slice() : []) || [];
			let contentActions = [];
			const widgetsSelected = dashboardApi && dashboardApi.getCanvas() && dashboardApi.getCanvas().getSelectedContentList().length;
			if (widgetsSelected) {
				contentActions = dashboardApi.getCanvas().getContentActionList([dashboardApi.getCanvas().getSelectedContentList()[0].getId()]);
			}
			widgetActions.unshift(...contentActions.map(action => {
				action['action'] = action.actions && action.actions.apply;
				return action;
			}));

			// Filter out certain actions in Explore.
			// Filtered out single widget actions: 'showDataBehindViz', 'formatAction', 'aggregationTypeAction'
			// Filtered out multiple widgets actions: 'alignWidget'
			// Filtered out dashboard specific content actions: 'duplicate', 'contextualGridAction', 'delete', 'orderToFront', 'orderToBack', 'EventGroups, 'FullScreen'
			// Filtered out lasso tool if two widgets are selected (compare card)
			// Filtered out launchExplore action
			// Filtered out dashboard panel actions (properties, slots, filters)
			const filteredOut = [
				...(widgetsSelected > 1 ? ['lasso'] : []),
				'showDataBehindViz',
				'formatAction',
				'aggregationTypeAction',
				'alignWidget',
				'contextualGridAction',
				'duplicate',
				'delete',
				'orderToFront',
				'orderToBack',
				'launchExplore',
				'filterDock',
				'properties',
				'slotEditor',
				'FullScreen',
				'EventGroups'
			];

			self.widgetActions = widgetActions.filter(action => filteredOut.indexOf(action.name) === -1);
		},
		toggleHidden(forceHidden){
			if(typeof forceHidden !== 'undefined'){
				self.isHidden = forceHidden;
			} else {
				self.isHidden = !self.isHidden;
			}
		},
		fetchItems() {
			self.setItems([]);
			self.setState('pending');

			const {
				dashboardApi
			} = getEnv(self);

			return getEnv(self).getToolBarItems(dashboardApi)
				.then(items => {
					self.setItems(items);
					self.setState('done');
				})
				.catch(() => {
					self.setState('error');
				});
		},
		setItems(items) {
			self.items = items;
		},
		setState(state) {
			self.state = state;
		}
	}));



// WEBPACK FOOTER //
// ./src/js/layout/explorecontainer/ExploreToolbarModel.js